import React, { useState, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { IconButton, Sorting as SortingIcon, Menu, Tooltip } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import { selectDashboardInfoSortingAndFilter } from 'features/dashboardInfo/selectors'
import { setSort } from 'features/dashboardInfo/dashboardInfoSlice'
import { DashboardInfoBaseState } from 'features/dashboardInfo/types'
import { AutomationId } from 'utils/automationUtil'
import { SORT_BY, SORT_ORDER } from 'features/dashboardInfo/constants'
import { SystemTheme } from 'hooks/useUiState/types'
import { heapTrack, HeapEvent, HeapEventPage } from 'utils/heapUtils'

import Divider from './Divider'

interface MenuItemClickEvent {
  target: {
    value: string
  }
}

type SortState = DashboardInfoBaseState['sort']

const SORT_BY_CONFIG = {
  name: {
    label: 'name',
    value: SORT_BY.NAME
  },
  labels: {
    label: 'label_other',
    value: SORT_BY.LABELS
  },
  dateCreated: {
    label: 'dateCreated',
    value: SORT_BY.DATE_CREATED
  },
  dateUpdated: {
    label: 'dateUpdated',
    value: SORT_BY.DATE_UPDATED
  },
  // scheduledReport: {
  //   label: 'scheduledReport',
  //   value: 'scheduledReport'
  // },
  liveReport: {
    label: 'liveReport',
    value: SORT_BY.LIVE_REPORTS
  },
  createdBy: {
    label: 'createdBy',
    value: SORT_BY.CREATED_BY
  }
}
const SORT_ORDER_CONFIG = {
  ascending: {
    label: 'ascending',
    value: SORT_ORDER.ASC
  },
  descending: {
    label: 'descending',
    value: SORT_ORDER.DESC
  }
}

const SortingHeader = styled.div<{ mode: Theme }>`
  padding: 5px 12px;
  font-size: ${sys.typescale[12].size};
  line-height: ${sys.typescale[12].lineHeight};
  font-weight: ${sys.fontWeight.semibold};
  color: ${({ mode }) => sys[mode].text.placeholder};
`

const buttonActiveStyle = css<{
  $theme: SystemTheme
}>`
  && {
    background-color: ${({ $theme }) => $theme.interaction.secondary.pressed};
  }
`

const StyledIconButton = styled(IconButton)<{
  $theme: SystemTheme
  $isMenuOpen: boolean
}>`
  ${({ $isMenuOpen }) => $isMenuOpen && buttonActiveStyle}
`

const StyledMenu = styled(Menu)<{ $theme: SystemTheme }>`
  & > div {
    background-color: ${({ $theme }) => $theme.elevation[300].background};
    box-shadow: ${({ $theme }) => $theme.elevation[300].boxShadow};
  }
`

const SortingButton = () => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLElement | null>(null)
  const mode = useDisplayTheme()
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { sort } = useAppSelector(selectDashboardInfoSortingAndFilter)
  const theme = useSysTheme()

  const handleSortByClick = (newSort: Partial<SortState>) => {
    setOpen(false)
    dispatch(setSort(newSort))
    heapTrack(HeapEvent.V4DashboardSortClick, {
      ...sort,
      ...newSort,
      page: HeapEventPage.DashboardView
    })
  }

  const handleButtonRef = (
    setTooltipTriggerRef: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  ) => (buttonElment: HTMLElement) => {
    buttonRef.current = buttonElment
    setTooltipTriggerRef(buttonElment)
  }

  return (
    <>
      <Tooltip
        mode={mode}
        content={<span>{t('sorting')}</span>}
        followCursor={false}
        offset={[0, 2]}
        callbackRefCreator={handleButtonRef}>
        <StyledIconButton
          mode={mode}
          color="secondary"
          variant="contained"
          $theme={theme}
          $isMenuOpen={open}
          onClick={() => setOpen(true)}>
          <SortingIcon />
        </StyledIconButton>
      </Tooltip>
      <StyledMenu
        mode={mode}
        $theme={theme}
        open={open}
        anchorEl={buttonRef.current}
        disablePortal
        placement="bottom-end"
        onClose={() => {
          setOpen(false)
        }}>
        <SortingHeader data-testid={AutomationId.menu.itemTitle} mode={mode}>
          {t('sortDashboardsBy')}
        </SortingHeader>
        {Object.values(SORT_BY_CONFIG).map((item) => (
          <Menu.MenuItem
            key={item.value}
            value={item.value}
            selected={item.value === sort.sortBy}
            onClick={(e: MenuItemClickEvent) => {
              handleSortByClick({ sortBy: e.target.value as SortState['sortBy'] })
            }}>
            {t(item.label)}
          </Menu.MenuItem>
        ))}
        <Divider mode={mode} />
        <SortingHeader data-testid={AutomationId.menu.itemTitle} mode={mode}>
          {t('sortOrder')}
        </SortingHeader>
        {Object.values(SORT_ORDER_CONFIG).map((item) => (
          <Menu.MenuItem
            key={item.value}
            value={item.value}
            selected={item.value === sort.order}
            onClick={(e: MenuItemClickEvent) =>
              handleSortByClick({
                order: e.target.value as SortState['order']
              })
            }>
            {t(item.label)}
          </Menu.MenuItem>
        ))}
      </StyledMenu>
    </>
  )
}

export default SortingButton
